import { useRef, useState, useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { useViewMode } from "@/shared/hooks/useViewMode";
import { useTimer } from "./hooks/useTimer";
import { useApplicationFilter } from "./hooks/useApplicationFilter";
import Sidebar from "./components/Sidebar/Sidebar";
import TrackedView, { TrackedViewHandle } from "./views/TrackedView";
import HistoryView from "./views/HistoryView";
import CalendarView from "./views/CalendarView/CalendarView";
import SettingsView from "./views/SettingsView";
import SupportView from "./views/SupportView/SupportView";
import FAQView from "@/shared/components/FAQ";
import styles from "./Attorney.module.css";
import HeaderBar from "./components/HeaderBar/HeaderBar";
import SummaryCards from "./components/SummaryCards/SummaryCards";
import { DashboardDataProvider, useDashboardData } from "@/shared/contexts/DashboardDataContext";
import { SettingsProvider, useSettings } from "@/shared/contexts/SettingsContext";
import AddEntryDialog, { AddEntryDialogHandle } from "./components/addDialog";
import { ExportValidationDialog, InvalidMatter } from "./components/ExportValidationDialog";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import toast from "react-hot-toast";
import ProcessingIndicator from "./components/ProcessingIndicator/ProcessingIndicator";

interface AttorneyPageContentProps {
  onSwitchToAdmin?: () => void;
}

function AttorneyPageContent({ onSwitchToAdmin }: AttorneyPageContentProps) {
  const { user } = useAuth();
  const { darkMode } = useDarkMode();
  const { viewMode, toggleViewMode } = useViewMode();
  const { makeApiRequest } = useApi();

  // Application filter hook - persists filter state to localStorage
  const {
    isVisible: isApplicationVisible,
    toggleApplication,
    filterEntries,
    isFilterActive,
  } = useApplicationFilter(user?.email || "");

  const [sidebarOpen] = useState(true);
  const [sortingMode, setSortingMode] = useState<'time'|'matter'>("time");
  const isClioFirm = false;
  const clioConnected = false;
  const { matters, tasks, activities, refreshRecords, isProcessingEntry, processingApplication } = useDashboardData();
  const { settings, refreshSettings } = useSettings();

  // Phase 2 state (timer + date)
  const [date, setDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });

  // Shared add entry dialog for all views (moved up for timer callback)
  const addDialogRef = useRef<AddEntryDialogHandle>(null);

  // Timer hook - opens add entry dialog with calculated duration when stopped
  const handleTimerStop = useCallback((seconds: number) => {
    const roundedUnits = Math.ceil(seconds / 360) / 10; // Round to nearest 0.1 hour
    if (addDialogRef.current?.openWithDuration) {
      addDialogRef.current.openWithDuration(roundedUnits);
    }
  }, []);

  const {
    elapsedSeconds,
    isRunning: timerRunning,
    start: startTimer,
    stop: stopTimer,
    reset: resetTimer,
  } = useTimer({ onStop: handleTimerStop });

  // Export validation dialog state (for Add and Export)
  const [exportValidationOpen, setExportValidationOpen] = useState(false);
  const [invalidMatters, setInvalidMatters] = useState<InvalidMatter[]>([]);
  const [pendingExportId, setPendingExportId] = useState<number | null>(null);

  const performExport = async (entryId: number) => {
    try {
      const exportResp = await makeApiRequest({ endpoint: "/records/exportRecords", method: "POST", body: { ids: [entryId] } });
      if (exportResp.error) throw new Error(String(exportResp.error));
      toast.success("Entry exported");
      await refreshRecords();
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'exportEntry', entryId });
      }
      toast.error("Error exporting entry");
    }
  };

  const onSaveNewEntry = async (
    newEntry: {
      id: number; date: string; matter: string; matter_id: string; description: string; duration: number; task: string; activity: string;
    },
    shouldExport: boolean
  ) => {
    try {
      const resp = await makeApiRequest({ endpoint: "/records/add", method: "POST", body: { ...newEntry, end_time: new Date().toLocaleTimeString() } });
      if (resp.error) throw new Error(String(resp.error));

      const newEntryId = resp.data?.id;

      // If shouldExport, validate first then export
      if (shouldExport && newEntryId) {
        // Validate export
        try {
          const validationResp = await makeApiRequest({ endpoint: "/records/validateExport", method: "POST", body: { ids: [newEntryId] } });
          if (validationResp.data?.invalidMatters?.length > 0) {
            // Show validation dialog - user can choose to proceed or cancel
            setPendingExportId(newEntryId);
            setInvalidMatters(validationResp.data.invalidMatters);
            setExportValidationOpen(true);
            toast.success("Entry added");
            await refreshRecords();
            return; // Don't export yet - wait for user decision
          }
        } catch {
          // Validation failed silently - proceed with export
        }

        // No invalid matters - proceed with export
        const exportResp = await makeApiRequest({ endpoint: "/records/exportRecords", method: "POST", body: { ids: [newEntryId] } });
        if (exportResp.error) throw new Error(String(exportResp.error));
        toast.success("Entry added and exported");
      } else {
        toast.success("Entry added");
      }

      await refreshRecords();
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'addEntry', shouldExport });
      }
      toast.error("Error adding entry");
    }
  };

  const updateHardcodedMatter = async (matterId: string) => {
    try {
      await makeApiRequest({
        endpoint: "/settings",
        method: "POST",
        body: {
          ...settings,
          hardcoded_matter: matterId,
        },
      });
      await refreshSettings();
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'updateHardcodedMatter', matterId });
      }
      toast.error("Failed to update hardcoded matter");
    }
  };

  // Selection + actions wired to TrackedView
  const [selectionCount, setSelectionCount] = useState(0);
  const trackedRef = useRef<TrackedViewHandle>(null);
  const onAddEntry = (durationUnits?: number) => {
    const units = durationUnits ?? 0;
    if (addDialogRef.current?.openWithDuration) addDialogRef.current.openWithDuration(units);
  };
  const onCombine = () => trackedRef.current?.combineSelected();
  const onDuplicate = () => trackedRef.current?.duplicateSelected();
  const onDelete = () => trackedRef.current?.deleteSelected();
  const onExport = () => trackedRef.current?.exportSelected();

  const renderView = () => {
    switch (viewMode) {
      case "entries":
        return null;
      case "history":
        return (
          <HistoryView
            date={date}
            sortingMode={sortingMode}
            onToggleSortingMode={() => setSortingMode((m) => (m === 'time' ? 'matter' : 'time'))}
            filterEntries={filterEntries}
          />
        );
      case "calendar":
        return (
          <CalendarView
            date={date}
            onSelectDate={(d) => {
              setDate(d);
              toggleViewMode('entries');
            }}
            onMonthChange={(d) => {
              setDate(d);
            }}
          />
        );
      case "settings":
        return <SettingsView />;
      case "support":
        return <SupportView />;
      case "faq":
        return <FAQView />;
      default:
        return null;
    }
  };

  return (
    <div className={`${styles.reportWrapper} ${darkMode ? styles.darkMode : ""}`}>
      <ProcessingIndicator
        show={isProcessingEntry}
        message="Processing entry..."
        application={processingApplication}
      />
      <AddEntryDialog
        ref={addDialogRef}
        onSave={onSaveNewEntry}
        matters={matters}
        tasks={tasks}
        activities={activities}
        currentDate={date}
        isInsuranceDefenseModeRestricted={false}
        checkForRedWords={false}
        hardcodedMatter={settings.hardcoded_matter}
      />
      <ExportValidationDialog
        open={exportValidationOpen}
        onOpenChange={setExportValidationOpen}
        invalidMatters={invalidMatters}
        onProceed={() => {
          setExportValidationOpen(false);
          if (pendingExportId) {
            performExport(pendingExportId);
          }
          setPendingExportId(null);
          setInvalidMatters([]);
        }}
        onCancel={() => {
          setExportValidationOpen(false);
          setPendingExportId(null);
          setInvalidMatters([]);
        }}
      />
      <Sidebar
        sidebarOpen={sidebarOpen}
        viewMode={viewMode as any}
        onChangeView={(v) => toggleViewMode(v)}
        isClioFirm={isClioFirm}
        clioConnected={clioConnected}
        onClioConnect={() => {}}
        onClearSelection={() => {}}
        onSwitchToAdmin={onSwitchToAdmin}
      />
      <div className={`${styles.reportWholeWrapper}`}>
        {viewMode !== 'settings' && viewMode !== 'support' && viewMode !== 'faq' && (
        <HeaderBar
          viewMode={viewMode as any}
          date={date}
          onDateChange={setDate}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          leftContent={<SummaryCards date={date} viewMode={viewMode as string} />}
          timerRunning={timerRunning}
          elapsedSeconds={elapsedSeconds}
          onStart={startTimer}
          onStop={stopTimer}
          onReset={resetTimer}
          selectionCount={selectionCount}
          onAddEntry={onAddEntry}
          onCombine={onCombine}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onExport={onExport}
          matters={matters}
          hardcodedMatter={settings.hardcoded_matter}
          onHardcodedMatterChange={updateHardcodedMatter}
          isApplicationVisible={isApplicationVisible}
          onToggleApplication={toggleApplication}
          isFilterActive={isFilterActive}
        />
        )}
        <div className={`${styles.reportContentWrapper} ${['settings', 'support', 'faq'].includes(viewMode) ? styles.fullBleed : ''}`}>
          {viewMode === 'entries' && (
            <TrackedView
              ref={trackedRef}
              date={date}
              dateRange={dateRange}
              sortingMode={sortingMode}
              onSelectionChange={setSelectionCount}
              onToggleSortingMode={() => setSortingMode((m) => (m === 'time' ? 'matter' : 'time'))}
              filterEntries={filterEntries}
            />
          )}
          {viewMode !== 'entries' && renderView()}
        </div>
      </div>
    </div>
  );
}

interface AttorneyPageProps {
  onSwitchToAdmin?: () => void;
}

export default function AttorneyPage({ onSwitchToAdmin }: AttorneyPageProps) {
  return (
    <DashboardDataProvider>
      <SettingsProvider>
        <AttorneyPageContent onSwitchToAdmin={onSwitchToAdmin} />
      </SettingsProvider>
    </DashboardDataProvider>
  );
}
